import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import Callout from "@/components/Callout";
import { RefreshCw, ExternalLink, Plus, X, CheckCircle2, Clock, AlertCircle, Ban, FileText } from "lucide-react";

const STATUS_ORDER = ["Open", "In Progress", "Blocked", "Done", "Out of Scope"];
const STATUS_META = {
  "Open":         { color: "#1D57A5", bg: "#EAF1FB", icon: Clock,         label: "Open" },
  "In Progress":  { color: "#B47700", bg: "#FFF6DE", icon: Clock,         label: "In Progress" },
  "Blocked":      { color: "#b91c1c", bg: "#FBE8E8", icon: AlertCircle,   label: "Blocked / Questions" },
  "Done":         { color: "#006747", bg: "#E0EFE8", icon: CheckCircle2,  label: "Done" },
  "Out of Scope": { color: "#5b6776", bg: "#EEEDEA", icon: Ban,           label: "Out of Scope" },
};
const OWNERS = ["", "Joey", "Katie", "Gerald", "Railtown"];

function daysAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso); const now = new Date();
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
}

function TaskCard({ task, onChange }) {
  const meta = STATUS_META[task.status] || STATUS_META["Open"];
  const Icon = meta.icon;
  const age = daysAgo(task.sent_date);
  const stalled = age != null && age > 30 && !["Done", "Out of Scope"].includes(task.status);
  const aging = age != null && age > 14 && age <= 30 && !["Done", "Out of Scope"].includes(task.status);
  return (
    <div className="bg-warm-tint border-l-4 p-4 mb-3"
         style={{ borderLeftColor: meta.color, boxShadow: stalled ? "0 0 0 2px #b91c1c33" : aging ? "0 0 0 2px #FFD10044" : "none" }}
         data-testid={`task-${task.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80 mb-1.5">
            <Icon className="w-3 h-3" style={{ color: meta.color }} />
            <span style={{ color: meta.color, fontWeight: 700 }}>{meta.label}</span>
            <span className="opacity-50">·</span>
            <span className="text-cobalt">{task.source}</span>
            {task.sent_date && (
              <>
                <span className="opacity-50">·</span>
                <span className="opacity-70">{task.sent_date}{age != null && ` (${age}d)`}</span>
              </>
            )}
            {stalled && <span className="ml-1 text-red-700 font-bold">STALLED</span>}
            {aging && <span className="ml-1 text-yellow font-bold bg-navy px-1">AGING</span>}
          </div>
          <div className="text-sm text-midnight leading-snug">{task.task_name}</div>
          {(task.comments || task.in_app_notes) && (
            <div className="mt-2 text-xs text-midnight opacity-70 italic border-l-2 border-cobalt pl-2">
              {task.in_app_notes || task.comments}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-warm flex flex-wrap items-center gap-2 text-xs">
        <select value={task.status}
                onChange={(e) => onChange(task.id, { status: e.target.value })}
                data-testid={`task-${task.id}-status`}
                className="bg-warm border border-navy/20 px-2 py-1 text-xs">
          {STATUS_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={task.owner || ""}
                onChange={(e) => onChange(task.id, { owner: e.target.value || null })}
                data-testid={`task-${task.id}-owner`}
                className="bg-warm border border-navy/20 px-2 py-1 text-xs">
          {OWNERS.map((o) => <option key={o} value={o}>{o || "— unassigned —"}</option>)}
        </select>
        {task.evidence_url ? (
          <a href={task.evidence_url} target="_blank" rel="noreferrer"
             className="text-evergreen inline-flex items-center gap-1 hover:underline">
            <ExternalLink className="w-3 h-3" /> evidence
          </a>
        ) : (
          <button onClick={() => {
            const url = window.prompt("Evidence URL (link to fixed page, GSC removal request, etc.)");
            if (url) onChange(task.id, { evidence_url: url });
          }}
                  className="text-cobalt hover:underline" data-testid={`task-${task.id}-evidence`}>
            + add evidence
          </button>
        )}
        <span className="ml-auto opacity-50 text-[10px]">{task.origin === "sheet:gerald" ? "from sheet" : "in-app"}</span>
      </div>
    </div>
  );
}

export default function SeoHomeworkPage() {
  const [items, setItems] = useState([]);
  const [sync, setSync] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ task_name: "", source: "In-app", owner: "" });

  const load = () => api.get("/seo/homework").then((r) => {
    setItems(r.data.items || []);
    setSync(r.data.sync);
  });

  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    const { data } = await api.patch(`/seo/homework/${id}`, patch);
    setItems((prev) => prev.map((t) => t.id === id ? data : t));
  };

  const runSync = async () => {
    setBusy(true);
    try { await api.post("/seo/homework/sync"); await load(); }
    finally { setBusy(false); }
  };

  const createTask = async () => {
    if (!draft.task_name.trim()) return;
    await api.post("/seo/homework", draft);
    setDraft({ task_name: "", source: "In-app", owner: "" });
    setAdding(false);
    await load();
  };

  const filtered = items.filter((t) =>
    (!ownerFilter || t.owner === ownerFilter) &&
    (!sourceFilter || t.source === sourceFilter)
  );
  const counts = useMemo(() => {
    const c = {};
    for (const s of STATUS_ORDER) c[s] = filtered.filter((t) => t.status === s).length;
    return c;
  }, [filtered]);
  const sources = useMemo(() => Array.from(new Set(items.map((t) => t.source))).sort(), [items]);

  return (
    <div data-testid="seo-homework-page">
      <TitleBlock
        eyebrow="SEO · between-call accountability"
        title="Homework Tracker"
        subtitle="Every task from Jason's coaching calls + Gerald/Katie's running list. Synced from Gerald's Google Sheet every 6 hours. Edit status, assign owners, attach evidence links — all in one place."
        right={
          <button onClick={runSync} disabled={busy} data-testid="seo-homework-sync"
                  className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            Sync sheet now
          </button>
        }
      />
      <div className="p-6 md:p-8 space-y-5">
        <Callout title="How to use this">
          <div className="text-sm text-midnight">
            Tasks come from <strong>Gerald&apos;s Google Sheet</strong> (auto-synced every 6h) plus anything you add here. Filter by owner to see only your work. <strong>Aging</strong> tasks ({'>'}14d old) are flagged yellow; <strong>stalled</strong> ({'>'}30d) are red — those are the ones to escalate. Click <em>+ add evidence</em> on a Done task to attach a verification link.
          </div>
        </Callout>

        <div className="flex flex-wrap items-center gap-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="opacity-60 uppercase tracking-widest text-[10px]">Owner:</span>
            <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}
                    data-testid="filter-owner"
                    className="bg-warm border border-navy/20 px-2 py-1">
              <option value="">All</option>
              {OWNERS.filter(Boolean).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="opacity-60 uppercase tracking-widest text-[10px]">Source:</span>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
                    data-testid="filter-source"
                    className="bg-warm border border-navy/20 px-2 py-1">
              <option value="">All</option>
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {sync?.last_synced && (
            <span className="ml-auto opacity-60">Last sheet sync: {new Date(sync.last_synced).toLocaleString()}</span>
          )}
          <button onClick={() => setAdding(!adding)}
                  className="inline-flex items-center gap-1 text-cobalt hover:underline"
                  data-testid="add-task-toggle">
            <Plus className="w-3.5 h-3.5" /> Add task
          </button>
        </div>

        {adding && (
          <div className="bg-warm-tint border-t-4 border-yellow p-4" data-testid="new-task-form">
            <div className="text-[10px] uppercase tracking-widest text-cobalt font-bold mb-2">New task (in-app)</div>
            <textarea value={draft.task_name}
                      onChange={(e) => setDraft({...draft, task_name: e.target.value})}
                      placeholder="e.g., Add JSON-LD schema to homepage"
                      data-testid="new-task-input"
                      className="w-full p-2 text-sm bg-warm border border-navy/20 outline-none focus:border-yellow"
                      rows={2} />
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <select value={draft.source} onChange={(e) => setDraft({...draft, source: e.target.value})}
                      className="bg-warm border border-navy/20 px-2 py-1">
                <option>In-app</option><option>Coach</option><option>Railtown</option>
                <option>GP</option><option>Other</option>
              </select>
              <select value={draft.owner} onChange={(e) => setDraft({...draft, owner: e.target.value})}
                      className="bg-warm border border-navy/20 px-2 py-1">
                {OWNERS.map((o) => <option key={o} value={o}>{o || "— unassigned —"}</option>)}
              </select>
              <button onClick={createTask} className="btn-yellow text-xs py-1 px-3" data-testid="new-task-create">Create</button>
              <button onClick={() => setAdding(false)} className="text-cobalt text-xs"><X className="w-3 h-3 inline" /></button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {STATUS_ORDER.slice(0, 3).map((status) => (
            <div key={status} data-testid={`col-${status.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-widest" style={{ color: STATUS_META[status].color }}>{status}</h3>
                <span className="text-xs opacity-60">{counts[status]}</span>
              </div>
              {filtered.filter((t) => t.status === status).map((t) => (
                <TaskCard key={t.id} task={t} onChange={update} />
              ))}
              {counts[status] === 0 && (
                <div className="text-xs opacity-40 italic text-center py-8 border-2 border-dashed border-navy/10">No tasks</div>
              )}
            </div>
          ))}
        </div>

        {/* Done + Out of Scope collapsed at bottom */}
        <div className="mt-6 pt-6 border-t-2 border-navy/10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {STATUS_ORDER.slice(3).map((status) => (
              <div key={status}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold uppercase tracking-widest opacity-60" style={{ color: STATUS_META[status].color }}>
                    <FileText className="w-3.5 h-3.5 inline mr-1" /> {status}
                  </h3>
                  <span className="text-xs opacity-60">{counts[status]}</span>
                </div>
                {filtered.filter((t) => t.status === status).slice(0, 4).map((t) => (
                  <TaskCard key={t.id} task={t} onChange={update} />
                ))}
                {counts[status] === 0 && (
                  <div className="text-xs opacity-40 italic">None yet</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
